import { getConnectorRootURL } from '@/config'
import { composedFetch } from './helpers'
import { errorIfInvalidResponse } from './errors'
import { getCurrentUserIdToken } from '@/services/auth'
import {
  SelectableContainerAndCodecOption,
  SelectableResolutionOption,
  OneToMany,
  SupportedContainerCode,
  SupportedCodecCode,
  containerCodeToNameMap,
  codecCodeToNameMap,
  TranscodeRequestData,
} from '@/store/types/transcoding'

import {
  listTranscodingJobsSchema,
  ApiTranscodingJob,
  captureInvalidSchemaOnSentry,
} from '@/api/schemas'

//                   _          _
//                  | |        | |
//                  | |__   ___| |_ __   ___ _ __ ___
//                  | '_ \ / _ \ | '_ \ / _ \ '__/ __|
//                  | | | |  __/ | |_) |  __/ |  \__ \
//                  |_| |_|\___|_| .__/ \___|_|  |___/
//                               | |
//                               |_|

export function getTranscodingURL(): string {
  return `${getConnectorRootURL()}/transcoding/`
}

/**
 * Get available output resolution presets.
 *
 * Used by transcoding modal to render available resolution presets.
 */
export function getTranscodeResolutionOptions(): SelectableResolutionOption[] {
  const separatorCharacter = '✕'
  const availableResolutions = ['640✕480', '1280✕720', '1920✕1080', '2560✕1440', '2048✕1008']

  const resolutionOptions = availableResolutions.map((label: string) => {
    // this assumes that A) there is a certain separator B) the dimmensions are given in this order
    const [width, height] = label
      .split(separatorCharacter)
      .map((dimension: string) => parseInt(dimension))

    return {
      label,
      width,
      height,
    }
  })

  return resolutionOptions
}

/**
 * Get available output container and codec presets.
 *
 * These are shown to the user.
 */
export function getTranscodeContainerAndCodecOptions(): SelectableContainerAndCodecOption[] {
  // represents which container codes go with which codec codes.
  // https://docs.aws.amazon.com/mediaconvert/latest/ug/reference-codecs-containers.html
  const rawContainerCodecCombinations: OneToMany<SupportedContainerCode, SupportedCodecCode> = {
    M2TS: ['H_264', 'H_265', 'MPEG2'],
    MP4: ['AV1', 'H_264', 'H_265'],
    F4V: ['H_264', 'MPEG2'],
    MXF: ['H_264', 'AVC_INTRA', 'MPEG2', 'VC3', 'XAVC'],
    MOV: ['H_264', 'MPEG2', 'PRORES'],
    WEBM: ['VP8', 'VP9'],

    // these containers where deemed unnecessary; left here as a reference to what we can once add:
    // CMFC: ['AV1', 'H_264', 'H_265'], // Same - CMAF?
    // MPD: ['AV1', 'H_264', 'H_265', 'VP8', 'VP9'], // MPEG-DASH
    // M3U8: ['H_264', 'H_265'], // Container from AWS API, does not see it in the compatibility table - is it HSL?
    // ISMV: ['H_264'],
    // RAW: ['AVC_INTRA', 'H_264', 'H_265', 'MPEG2', 'VC3', 'XAVC'],
  }

  const options = []

  for (const containerCode in rawContainerCodecCombinations) {
    for (const codecCode of rawContainerCodecCombinations[
      containerCode as SupportedContainerCode
    ]) {
      // construct options by pairing container with a supported codec and creating a human-readable label
      options.push({
        label: `${containerCodeToNameMap[containerCode as SupportedContainerCode]} ${
          codecCodeToNameMap[codecCode]
        }`,
        codec: codecCode,
        container: containerCode as SupportedContainerCode,
      })
    }
  }

  return options
}

/**
 * Used by the file browser to determine whether to show the transocing option.
 * It is, in fact, a mapping of AWS supported input extensions, and is kept here
 * because of this tight coupling with the api.
 *
 * NOTE! It naively checks the file extensions, meaning:
 * 1) that it can break if there is a dot character signifying something other than file extension;
 * 2) it is not aware of underlying codecs, meaning it can throw later, deep within the service;
 *
 * @param fileName A book to be judged by its cover
 * @returns the vertict for should the file be considered transcodable
 */
export function isTranscodableContainer(fileName: string): boolean {
  if (!fileName) {
    return false
  }

  const dotDelimitedArray = fileName.split('.')

  if (dotDelimitedArray.length === 1) return true

  const possibleExtension = dotDelimitedArray.pop()?.toLowerCase()

  const ACCEPTABLE_AWS_INPUT_CONTAINERS = [
    '3g2',
    '3gp',
    'asf',
    'wmv',
    'avi',

    // not sure which extensions are meant by "Adobe Flash" in the AWS docs
    'fla', // adobe flash source file
    'swf', // adobe flash movie / "shockwave flash"

    // MPEG-2
    'mpg',
    'mpeg',

    // MPEG-4
    'mp4',
    'f4v',

    'mxf',
    'mov', // Apple QuickTime
    'mkv', // Matroska video
    'webm',
  ]

  // is roughly transcodable if it lacks extension (no-container) or is acceptable by AWS
  return !possibleExtension || ACCEPTABLE_AWS_INPUT_CONTAINERS.includes(possibleExtension)
}

//                    ___  ______ _____                                  _
//                   / _ \ | ___ \_   _|                                | |
//                  / /_\ \| |_/ / | |    _ __ ___  __ _ _   _  ___  ___| |_ ___
//                  |  _  ||  __/  | |   | '__/ _ \/ _` | | | |/ _ \/ __| __/ __|
//                  | | | || |    _| |_  | | |  __/ (_| | |_| |  __/\__ \ |_\__ \
//                  \_| |_/\_|    \___/  |_|  \___|\__, |\__,_|\___||___/\__|___/
//                                                    | |
//                                                    |_|

/**
 * The API hit, takes form data and adjusts its shape to comply with endpoint, hits.
 * @param options the options form the modal form
 * @returns api response
 */
export async function apiTranscodeFile(requestData: TranscodeRequestData): Promise<void> {
  const userToken = await getCurrentUserIdToken()

  const response = await composedFetch('POST', getTranscodingURL(), userToken, requestData)

  await errorIfInvalidResponse(response)
}

/**
 * @todo not used - for live state updates, we bind instead of fetch
 * @returns An array of Transcoded Jobs
 */
export async function apiListTranscodingJobs(): Promise<ApiTranscodingJob[]> {
  const response = await composedFetch('GET', getTranscodingURL(), await getCurrentUserIdToken())

  await errorIfInvalidResponse(response)
  const json = await response.json()

  captureInvalidSchemaOnSentry(json, listTranscodingJobsSchema)

  return json.user_jobs
}

// TODO we could type the return value as Trans
export async function apiDeleteTranscodingJob(uuid: string): Promise<void> {
  const response = await composedFetch(
    'DELETE',
    `${getTranscodingURL()}${uuid}/`,
    await getCurrentUserIdToken(),
  )

  await errorIfInvalidResponse(response)
}
